import { useState, useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { addItem } from "../store/cartSlice.js";
import { fetchProducts } from "../store/productsSlice.js";
import { toast } from "react-toastify";

const SpecialOfferPopup = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const products = useSelector((s) => s.products.items || []);
  const user = useSelector((s) => s.auth.user);
  const [show, setShow] = useState(false);
  const [product, setProduct] = useState(null);

  useEffect(() => {
    dispatch(fetchProducts());
  }, [dispatch]);

  useEffect(() => {
    if (!products.length) return;

    // Pick the product with the highest discount %
    const discounted = products
      .filter((p) => p.oldPrice && p.price && p.oldPrice > p.price)
      .sort((a, b) => {
        const da = ((a.oldPrice - a.price) / a.oldPrice) * 100;
        const db2 = ((b.oldPrice - b.price) / b.oldPrice) * 100;
        return db2 - da;
      });

    if (!discounted.length) return;

    setProduct(discounted[0]);

    const timer = setTimeout(() => setShow(true), 8000); // show after 8s
    return () => clearTimeout(timer);
  }, [products]);

  if (!show || !product) return null;

  const discount = Math.round(((product.oldPrice - product.price) / product.oldPrice) * 100);
  const saving = product.oldPrice - product.price;
  const image = product.images?.[0] || product.image;

  const handleAddToCart = () => {
    if (!user) {
      toast.info("Please sign in to purchase");
      navigate("/signin");
      setShow(false);
      return;
    }
    dispatch(addItem({ id: product.id, name: product.name, price: product.price, image }));
    toast.success(`${product.name} added to cart!`);
    setShow(false);
  };

  return (
    <div
      onClick={() => setShow(false)}
      style={{
        position: "fixed", inset: 0,
        background: "rgba(0,0,0,0.55)",
        zIndex: 2000,
        display: "flex", alignItems: "center", justifyContent: "center",
        padding: "16px",
        animation: "popupFadeIn 0.3s ease",
      }}
    >
      <style>{`
        @keyframes popupFadeIn { from { opacity:0 } to { opacity:1 } }
        @keyframes popupSlideUp { from { opacity:0; transform:translateY(40px) } to { opacity:1; transform:translateY(0) } }
        .popup-box { animation: popupSlideUp 0.35s cubic-bezier(0.34,1.56,0.64,1); }
        .popup-grab-btn:hover { opacity: 0.92; transform: translateY(-1px); }
      `}</style>

      <div
        className="popup-box"
        onClick={(e) => e.stopPropagation()}
        style={{
          background: "#fff",
          borderRadius: 20,
          width: "100%",
          maxWidth: 400,
          overflow: "hidden",
          boxShadow: "0 24px 60px rgba(0,0,0,0.25)",
          position: "relative",
        }}
      >
        {/* Close button */}
        <button
          onClick={() => setShow(false)}
          style={{
            position: "absolute", top: 12, right: 12,
            width: 32, height: 32, borderRadius: "50%",
            background: "rgba(0,0,0,0.15)", border: "none",
            color: "#fff", fontSize: 14, cursor: "pointer",
            display: "flex", alignItems: "center", justifyContent: "center",
            zIndex: 10, lineHeight: 1,
          }}
        >
          <i className="bi bi-x-lg"></i>
        </button>

        {/* Product image banner */}
        <div style={{ position: "relative", height: 200, background: "#f5f0eb", overflow: "hidden" }}>
          {image ? (
            <img src={image} alt={product.name}
              style={{ width: "100%", height: "100%", objectFit: "cover" }} />
          ) : (
            <div style={{ display: "flex", alignItems: "center", justifyContent: "center", height: "100%" }}>
              <i className="bi bi-bag" style={{ fontSize: 64, color: "#ccc" }}></i>
            </div>
          )}
          {/* Discount pill over image */}
          <div style={{
            position: "absolute", bottom: 12, left: 12,
            background: "#e53935", color: "#fff",
            borderRadius: 20, padding: "4px 14px",
            fontWeight: 700, fontSize: 15,
          }}>
            {discount}% OFF
          </div>
        </div>

        {/* Body */}
        <div style={{ padding: "20px 24px 24px" }}>
          {/* Label */}
          <div style={{ fontSize: 11, fontWeight: 700, color: "#984A1F", letterSpacing: 1, textTransform: "uppercase", marginBottom: 6 }}>
            🔥 Limited Time Deal
          </div>

          {/* Product name */}
          <div style={{ fontSize: 17, fontWeight: 700, color: "#1a1a1a", marginBottom: 4, lineHeight: 1.3 }}>
            {product.name}
          </div>
          {product.description && (
            <div style={{ fontSize: 12, color: "#888", marginBottom: 14,
              display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical", overflow: "hidden" }}>
              {product.description}
            </div>
          )}

          {/* Price row */}
          <div style={{ display: "flex", alignItems: "baseline", gap: 10, marginBottom: 6 }}>
            <span style={{ fontSize: 24, fontWeight: 800, color: "#2e7d32" }}>
              ₹{product.price.toLocaleString()}
            </span>
            <span style={{ fontSize: 14, color: "#aaa", textDecoration: "line-through" }}>
              ₹{product.oldPrice.toLocaleString()}
            </span>
          </div>
          <div style={{ fontSize: 12, color: "#2e7d32", fontWeight: 600, marginBottom: 20 }}>
            You save ₹{saving.toLocaleString()} on this order
          </div>

          {/* Buttons */}
          <button
            className="popup-grab-btn"
            onClick={handleAddToCart}
            style={{
              width: "100%", padding: "13px",
              background: "linear-gradient(135deg,#984A1F,#7a3b19)",
              color: "#fff", border: "none", borderRadius: 12,
              fontWeight: 700, fontSize: 15, cursor: "pointer",
              transition: "all 0.2s", marginBottom: 10,
            }}
          >
            <i className="bi bi-cart-plus-fill me-2"></i>Add to Cart
          </button>
          <button
            onClick={() => { navigate(`/product/${product.id}`); setShow(false); }}
            style={{
              width: "100%", padding: "11px",
              background: "transparent", color: "#984A1F",
              border: "1.5px solid #984A1F", borderRadius: 12,
              fontWeight: 600, fontSize: 14, cursor: "pointer",
            }}
          >
            View Product
          </button>

          {/* Trust row */}
          <div style={{ display: "flex", justifyContent: "center", gap: 20, marginTop: 16 }}>
            {[["shield-check", "Authentic"], ["truck", "Free Delivery"], ["arrow-return-left", "Easy Returns"]].map(([icon, label]) => (
              <div key={label} style={{ display: "flex", alignItems: "center", gap: 4, fontSize: 11, color: "#999" }}>
                <i className={`bi bi-${icon}`}></i>{label}
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default SpecialOfferPopup;
